/**
 * FeeSuggestions — the firm's rate card resolved for one case, offered as a prefill for the
 * payment-amount field (Lloyd, Jul 20: "prefilled but editable").
 *
 * Design rule this follows: a suggestion is never presented as a decision. Every amount shows the
 * arithmetic behind it, anything the backend could not derive is marked "confirm" instead of being
 * quietly rounded to a plausible number, and nothing is applied until staff click Use.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Stack,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import PaymentApi, {
  FeeSuggestionDto,
  FeeSuggestionsDto,
  PaymentsForbiddenError,
} from '../../api/PaymentApi';
import { formatCentsUsd } from '../../constants/paymentPresets';

interface FeeSuggestionsProps {
  Ticket_guid: string;
  /** Fills the amount field, in whole dollars, matching the field's own format. */
  onUseAmount: (dollars: string) => void;
  /**
   * Bumped by the parent whenever the amount on record is saved. That amount feeds the appeal
   * credit, so a stale panel would keep showing a fee derived from the previous figure.
   */
  refreshKey?: number;
}

/**
 * Whether this fee is the one the case can actually use. A court of record means the client CANNOT
 * appeal to county and a jury trial is the only route; a non-record court means the reverse. Quoting
 * the wrong one tells a client they have an option they do not have, so the inapplicable fee is
 * labelled rather than left looking equally valid. Unknown never collapses to either.
 */
function applicabilityNote(kind: string, isCourtOfRecord: boolean | null): string | null {
  if (isCourtOfRecord === null) return null;
  if (kind === 'appeal' && isCourtOfRecord) {
    return 'Court of record — this client cannot appeal to county.';
  }
  if (kind === 'jury' && !isCourtOfRecord) {
    return 'Not a court of record — appealing to county is the usual route.';
  }
  return null;
}

function SuggestionRow({
  suggestion,
  isCourtOfRecord,
  onUseAmount,
}: {
  suggestion: FeeSuggestionDto;
  isCourtOfRecord: boolean | null;
  onUseAmount: (dollars: string) => void;
}) {
  const { kind, label, suggestedCents, basis, requiresManualEntry } = suggestion;
  const note = applicabilityNote(kind, isCourtOfRecord);

  return (
    <Box sx={{ py: 1 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 170 }}>
          {label}
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {suggestedCents === null ? '—' : formatCentsUsd(suggestedCents)}
        </Typography>

        {requiresManualEntry && (
          <Tooltip title="No confirmed rate for this combination — check the amount before using it.">
            <Chip label="confirm" size="small" color="warning" variant="outlined" />
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        {/* $0 means "covered, do not charge" — there is no link to create, so no Use button. */}
        {suggestedCents !== null && suggestedCents > 0 && (
          <Button size="small" onClick={() => onUseAmount(String(suggestedCents / 100))}>
            Use
          </Button>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {basis}
      </Typography>

      {note && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
          {note}
        </Typography>
      )}
    </Box>
  );
}

const FeeSuggestions: React.FC<FeeSuggestionsProps> = ({
  Ticket_guid,
  onUseAmount,
  refreshKey = 0,
}) => {
  const [data, setData] = useState<FeeSuggestionsDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSuggestions = useCallback(async () => {
    if (!Ticket_guid) return;
    setLoading(true);
    setError(null);
    try {
      setData(await PaymentApi.getFeeSuggestions(Ticket_guid));
    } catch (err) {
      // A manager without payment access simply does not see this section — not an error state.
      if (!(err instanceof PaymentsForbiddenError)) {
        setError(err instanceof Error ? err.message : 'Failed to load suggested fees');
      }
    } finally {
      setLoading(false);
    }
  }, [Ticket_guid, refreshKey]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }

  if (!data) return null;

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Suggested fees
        </Typography>
        <Chip
          label={data.source ? data.source.replace(/_/g, ' ') : 'no referral source'}
          size="small"
          variant="outlined"
          color={data.source ? 'default' : 'warning'}
        />
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {data.paymentsNote}
      </Typography>

      {data.suggestions.map((s) => (
        <SuggestionRow
          key={s.kind}
          suggestion={s}
          isCourtOfRecord={data.isCourtOfRecord}
          onUseAmount={onUseAmount}
        />
      ))}

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
        Suggestions only — the amount you charge is whatever you enter below.
      </Typography>

      <Divider sx={{ my: 2 }} />
    </>
  );
};

export default FeeSuggestions;
